import { useMemo, useCallback, useEffect, useState } from 'react';
import { Col, Row, Form, Badge } from 'antd';
import { Link, useLocation } from 'react-router-dom';
import { Button } from 'components';
import { DeleteIcon, EditIcon } from 'icons';
import { useTranslation } from 'react-i18next';
import { EyeOutlined } from '@ant-design/icons';
import { useRecoilValue } from 'recoil';
import { getPage } from 'hooks/useUser';
import UIHelper from 'helpers/UIHelper';
import ErrorHelper from 'helpers/ErrorHelper';
import { useNavigate } from 'hooks/useNavigate';
import { useParams } from 'react-router-dom';
import { useApi } from 'hooks/useApi';
import moment from 'moment';
import { useFilter } from 'hooks/useFilter';
import AssetApi from 'libs/api/leasing/asset';
import VisitCardApi from 'libs/api/leasing/visitCard';

const api = new VisitCardApi();
const assetApi = new AssetApi();
/**
 *
 * @param {boolean} isMainPage - true if the page loads all items, false if just one
 * @returns {{onChangeTableFilter: (function(*, *, *): void), data: {pagination: *&{current: number, hideOnSinglePage: boolean, pageSize: number, position: [string], showSizeChanger: boolean}, sorting: {field: string, order: string}}, columns: [{dataIndex: string, fixed: string, title: TFuncReturn<"translation", string, string, undefined>},{dataIndex: string, title: TFuncReturn<"translation", string, string, undefined>},{dataIndex: string, title: TFuncReturn<"translation", string, string, undefined>},{dataIndex: string, title: TFuncReturn<"translation", string, string, undefined>},{dataIndex: string, title: TFuncReturn<"translation", string, string, undefined>},null], handleDownloadExcel: ((function(): Promise<void>)|*), singleItem: {}, filters: [{values: {title: *, value: *}[], name: string, title: TFuncReturn<"translation", string, string, undefined>, type: string},{values: [{title: string, value: string},{title: string, value: string},{title: string, value: string}], name: string, title: TFuncReturn<"translation", string, string, undefined>, type: string},{values: [{title: string, value: string},{title: string, value: string}], name: string, title: TFuncReturn<"translation", string, string, undefined>, type: string},{values: [{title: string, value: string},{title: string, value: string}], name: string, title: TFuncReturn<"translation", string, string, undefined>, type: string},{values: [{title: string, value: string},{title: string, value: string}], name: string, title: TFuncReturn<"translation", string, string, undefined>, type: string},null,null,null], loading: boolean, onDelete: ((function(*): Promise<void>)|*), updateInProcess: boolean, companies: ([]|*[]), form: FormInstance<any>, handleCreateOrUpdate: ((function(*): Promise<void>)|*), asset: *[]}} state and callbacks for ExperienceYears page
 */

const useVisitCard = (isMainPage = true) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const pages = useRecoilValue(getPage);
  const { filterData } = useFilter();
  const [disabledData, setDisabledData] = useState(true);
  const [showEditDataModal, setShowEditDataModal] = useState(false);
  const [nextVisitTypeValue, setNextVisitTypeValue] = useState('');
  const [visitTypeDisabled, setVisitTypeDisabled] = useState(false);
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { id } = useParams();
  const leasingsMeetingTypes = filterData?.leasings_meeting_types || [];

  const statuses = useMemo(
    () => ({
      in_progress: 'In corso',
      done: 'Conclusa',
      resend: 'Rinviata',
      failed: 'Fallita',
      not_interested: 'Non interessati',
    }),
    [],
  );

  const types = useMemo(
    () => ({
      offer: 'Proposta commerciale',
      credits: 'Recupero crediti',
      interlocutory: 'Interlocutorio',
    }),
    [],
  );

  const {
    loading,
    data,
    singleItem,
    onDelete,
    onChangeTableFilter,
    updateInProcess,
    setUpdateInProcess,
    handleDownloadExcel,
  } = useApi({
    resourceKey: 'visit_cards',
    singleResourceKey: 'visit_card',
    api,
    isMainPage,
  });

  const columns = useMemo(() => {
    return [
      {
        title: t('Data'),
        dataIndex: 'visit_date',
        fixed: 'left',
        render: (value) => value && moment(value).format('DD-MMMM-YYYY'),
      },
      {
        title: t('Azienda'),
        dataIndex: 'company_title',
      },
      // {
      //   title: t('Cognome contatto'),
      //   dataIndex: 'leasings_contact_surname',
      // },
      {
        title: t('Asset proposto'),
        dataIndex: 'leasings_asset',
      },
      {
        title: t('Unità proposta'),
        dataIndex: 'leasings_room',
      },
      {
        title: t('Stato trattativa'),
        dataIndex: 'status',
        render: (value) => statuses[value],
      },
      {
        title: t('Leasing manager'),
        dataIndex: 'leasings_manager',
        render: (data) => {
          return <div>{data?.name}</div>;
        },
      },
      // {
      //   title: t('Tipo di incontro'),
      //   dataIndex: 'leasings_meeting_type',
      //   render: (value) => value,
      // },
      {
        title: t('Dati economici discussi'),
        dataIndex: 'data_exist',
        render: (value) =>
          value ? <Badge status='success' /> : <Badge status='error' />,
      },
      {
        title: t('Allegati'),
        dataIndex: 'files_exist',
        render: (value) =>
          value ? <Badge status='success' /> : <Badge status='error' />,
      },

      {
        title: '',
        dataIndex: 'address',
        align: 'right',
        width: 160,
        fixed: 'right',
        render: (data, row = { can_update: false, can_destroy: false }) =>
          (pages?.ability?.can_read !== false ||
            row.can_read !== false ||
            row.can_update ||
            row.can_destroy !== false) && (
            <Row
              gutter={16}
              justify={'end'}
              onClick={(e) => e.stopPropagation()}
            >
              {(pages?.ability?.can_read !== false ||
                row.can_read !== false) && (
                <Col>
                  <Link to={`${pathname}/${row.id}`}>
                    <Button type={'secondary'} icon={<EyeOutlined />} />
                  </Link>
                </Col>
              )}
              {row.can_update && (
                <Col>
                  <Link to={`${pathname}/${row.id}/edit`}>
                    <Button type={'secondary'} icon={<EditIcon />} />
                  </Link>
                </Col>
              )}
              {row.can_destroy !== false && (
                <Col>
                  <Link to={`${pathname}/${row.id}/delete`}>
                    <Button type={'red'} icon={<DeleteIcon />} />
                  </Link>
                </Col>
              )}
            </Row>
          ),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, statuses, types, pages?.ability?.can_read]);

  const handleCreateOrUpdate = useCallback(
    async (values) => {
      console.log('values=', values);
      setUpdateInProcess(true);

      const formData = new FormData();
      Object.keys(values).forEach((key) => {
        if (
          key === 'first_file' ||
          key === 'second_file' ||
          key === 'hot_file'
        ) {
          if (values[key] && values[key][0]?.originFileObj) {
            formData.append(
              `visit_card[${key}]`,
              values[key][0]?.originFileObj,
            );
          }
        } else {
          formData.append(`visit_card[${key}]`, values[key]);
        }
      });

      try {
        if (!id) {
          await api.create({
            data: formData,
            headers: { 'Content-Type': 'multipart/Form-data' },
          });
          navigate(UIHelper.getListRoute(pathname, 1));
        } else {
          await api.put(id, {
            data: formData,
            headers: { 'Content-Type': 'multipart/Form-data' },
          });
          navigate(UIHelper.getListRoute(pathname));
        }
      } catch (e) {
        const errors = e.response.data || {};
        if (errors.status !== 500) {
          form.setFields(
            ErrorHelper.getErrorsForForm(errors, {
              company: 'company_id',
              leasings_asset: 'leasings_asset_id',
              leasings_contact: 'leasings_contact_id',
              leasings_manager: 'leasings_manager_id',
              leasings_room: 'leasings_room_id',
            }),
          );
        }
      } finally {
        setUpdateInProcess(false);
      }
    },
    [setUpdateInProcess, form, navigate, pathname, id],
  );

  useEffect(() => {
    if (id && !isMainPage && singleItem) {
      let fieldsValues = [];
      for (let key in singleItem) {
        switch (key) {
          case 'visit_date': {
            fieldsValues.push({
              name: key,
              value: moment(singleItem[key], 'YYYY-MM-DD'),
            });
            break;
          }
          case 'leasings_manager':
          case 'manager_user_id':
            break;
          default: {
            if (singleItem[key]) {
              fieldsValues.push({
                name: key,
                value: singleItem[key],
              });
            }
          }
        }
      }
      if (singleItem.leasings_manager) {
        fieldsValues.push({
          name: 'leasings_manager_id',
          value: singleItem.leasings_manager?.id,
        });
      }
      const findContact = filterData.leasings_contacts.find(
        (item) => item.value === +singleItem.leasings_contact_id,
      );
      fieldsValues.push({
        name: 'company_name',
        value: findContact?.company_title,
      });
      fieldsValues.push({
        name: 'leasings_meeting_type_id',
        value: singleItem.leasings_meeting_type?.id,
      });
      form.setFields(fieldsValues);
      setDisabledData(!singleItem?.is_edit_data);
      // if (['credits', 'interlocutory'].includes(singleItem.visit_type)) {
      //   setVisitTypeDisabled(true);
      // } else {
      //   setVisitTypeDisabled(false);
      // }
    }
  }, [singleItem, form, id, isMainPage, filterData?.leasings_contacts]);

  const filters = useMemo(
    () => [
      {
        title: t('Azienda'),
        type: 'select',
        name: 'by_leasings_contact',
        values: filterData?.leasings_contacts?.map((company) => ({
          title: company.company_title,
          value: company.value,
        })),
      },
      {
        title: t('Unità proposta'),
        type: 'select',
        name: 'by_leasings_room',
        values: filterData?.leasings_rooms?.map((item) => ({
          title: item.title,
          value: item.value,
        })),
      },
      {
        title: t('Asset proposto'),
        type: 'select',
        name: 'by_leasings_asset',
        values: filterData?.leasings_assets?.map((item) => ({
          title: item.title,
          value: item.value,
        })),
      },
      // {
      //   title: t('Cognome'),
      //   type: 'input',
      //   name: 'by_surname',
      // },
      {
        title: t('Leasing Manager'),
        type: 'select',
        name: 'by_manager_user_id',
        values: filterData?.leasings_managers?.map((item) => ({
          title: item.title,
          value: item.value,
        })),
      },

      {
        title: t('Status'),
        type: 'select',
        name: 'by_status',
        values: Object.keys(statuses).map((key) => ({
          title: statuses[key],
          value: key,
        })),
      },

      {
        title: t('Tipo di incontro'),
        type: 'select',
        name: 'by_leasings_meeting_type_id',
        values: filterData?.leasings_meeting_types?.map((item) => ({
          title: item.title,
          value: item.value,
        })),
      },
      {
        title: t('Dati economici discussi'),
        type: 'select',
        name: 'by_data_exist',
        values: [
          {
            title: 'Si',
            value: 1,
          },
          {
            title: 'No',
            value: 0,
          },
        ],
      },
      {
        title: t('Allegati'),
        type: 'select',
        name: 'by_files_exist',
        values: [
          {
            title: 'Si',
            value: 1,
          },
          {
            title: 'No',
            value: 0,
          },
        ],
      },
      {
        title: t('Data inizio'),
        type: 'date',
        name: 'from_visit_date',
        values: [],
      },
      {
        title: t('Data fine'),
        type: 'date',
        name: 'to_visit_date',
        values: [],
      },
    ],
    [t, filterData, statuses, types],
  );

  return {
    form,
    columns,
    data,
    singleItem,
    loading,
    onDelete,
    updateInProcess,
    handleCreateOrUpdate,
    onChangeTableFilter,
    asset: filterData.leasings_assets || [],
    leasingsManagers: filterData.leasings_managers || [],
    leasingsRooms: filterData.leasings_rooms || [],
    leasingsContacts: filterData.leasings_contacts || [],
    filters,
    handleDownloadExcel,
    companies: filterData?.companies || [],
    setDisabledData,
    disabledData,
    visitTypeDisabled,
    setVisitTypeDisabled,
    showEditDataModal,
    setShowEditDataModal,
    nextVisitTypeValue,
    setNextVisitTypeValue,
    leasingsMeetingTypes,
  };
};

export default useVisitCard;
